import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { CodeBlock } from '../components/docs/CodeBlock';
import { SearchPlayground } from '../components/docs/SearchPlayground';
import { BILLING_LIVE } from '../config';
import { CHEAPEST_PAID_TIER, FREE_TIER, PLAN_CURRENCY } from '../data/plans';
import { SAMPLE_SEARCH_JSON } from '../data/sample-search-response';
import { useDocumentHead } from '../hooks/useDocumentHead';
import { formatV1SearchCurl, LANDING_SAMPLE_FORM } from '../lib/sample';

/**
 * Strangers land here. Signed-in visitors are redirected to `/app` in App.tsx.
 *
 * ── Order ──────────────────────────────────────────────────────────────────
 *
 * Claim, then the numbers behind it, then a live table proving the numbers,
 * and only then the request shape. The demo used to sit above the buttons,
 * which meant the page asked for a decision before it had shown anything; it
 * now sits directly under the terms it is evidence for.
 *
 * ── Spacing ────────────────────────────────────────────────────────────────
 *
 * Sections are not evenly spaced. Hero → stats → demo are one argument and are
 * set close; everything after is a separate question the reader may or may not
 * have, and gets `mt-24` to say so. Uniform gaps between every block is how a
 * page ends up reading as a list of blocks.
 *
 * No latency or uptime claims until deploy-checklist measurements exist.
 */

/**
 * Six facts a developer checks before they read a single line of the docs.
 *
 * The first two used to read "Free, no card" beside "Per month after", which
 * describes a trial: a free window, then a bill. The free tier does not expire
 * (`PLAN_TIERS` gives it `priceDetail: 'forever'`), so the labels now say the
 * word "forever" outright and name the paid cell as the cheapest plan rather
 * than as what happens next. Nothing sequential in either label.
 *
 * While `BILLING_LIVE` is false, the price cell must not read as "buy now".
 * /pricing only has waitlist CTAs for paid tiers then; promising "$9 start"
 * without saying waitlist is the same lie in reverse of hiding prices.
 */
const SPECS: { value: string; label: string }[] = [
    { value: FREE_TIER.requests, label: 'Free forever, no card' },
    {
        value: `${CHEAPEST_PAID_TIER.priceLabel} ${PLAN_CURRENCY}/mo`,
        // Live: ordinary floor price. Waitlist: same number, labeled so a
        // reader who needs more than free today knows they are joining a list
        // rather than checking out.
        label: BILLING_LIVE ? 'Paid plans start at' : 'from (waitlist)',
    },
    { value: '200', label: 'Rows per request' },
    { value: '60', label: 'Requests per minute' },
    { value: 'Live', label: 'Never cached' },
    { value: 'All tiers', label: 'Commercial use' },
];

/**
 * Prose, not accordions. Five `<details>` elements is five identical grey
 * rectangles that hide five answers worth reading, and nobody clicks the
 * fourth one. These are short enough to just say.
 *
 * Questions are phrased the way a buyer would ask them, and answered without
 * naming our internals. "Fixture pages are asserted in parser tests with
 * measured fill rates" is true and is the right sentence for a teammate; the
 * person reading this wants to know whether their integration breaks.
 */
const FAQ: { question: string; answer: ReactNode }[] = [
    {
        question: "Why not use eBay's own API?",
        answer: (
            <>
                eBay&apos;s Finding API restricts sold access and new developer approval is slow. ActualSold needs no
                eBay developer account. One request is a live public results page returned as JSON.
            </>
        ),
    },
    {
        question: 'How fresh is the data?',
        answer: (
            <>
                Every call fetches from eBay at the moment you make it. Nothing is served from a cache. The sample
                above is the one exception, and it refreshes once a day.
            </>
        ),
    },
    {
        question: 'What if eBay changes their site?',
        answer: (
            <>
                Then we fix it, and usually before you notice. Every change we make is tested against saved eBay pages
                that would fail the moment rows started coming back thin. We would rather tell you that than promise a
                repair time we cannot guarantee.
            </>
        ),
    },
    {
        question: 'Can I use it commercially?',
        answer: <>Yes, on every tier including free. What you build on top of the rows is yours.</>,
    },
    {
        question: 'What counts as one request?',
        answer: <>One page of up to 200 rows, whether it returns 10 or 200. Pagination bills per page.</>,
    },
    {
        question: 'How do I get started?',
        answer: (
            <>
                Sign in with a magic link, issue a key, call{' '}
                <code className="font-mono text-xs text-text">GET /v1/search</code>. The free tier needs no card.{' '}
                {BILLING_LIVE
                    ? 'Upgrade from the pricing page whenever you outgrow it.'
                    : 'Paid plans are not open yet. See pricing to join the list for a tier.'}
            </>
        ),
    },
];

export function LandingPage() {
    useDocumentHead({
        title: 'ActualSold: real eBay sold prices as JSON',
        description:
            'One request returns up to 200 completed eBay sales, price, date, condition, shipping. Live, uncached. Free tier, no card.',
        canonicalPath: '/',
    });

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://actualsold.com';
    const curlSample = formatV1SearchCurl(origin, LANDING_SAMPLE_FORM, 60);

    return (
        <div className="pb-8">
            <section className="max-w-3xl">
                <p className="eyebrow text-text-muted">Marketplace-data API</p>
                <h1 className="mt-5 max-w-[16ch] text-hero text-balance">Real eBay sold prices, as clean JSON.</h1>
                <p className="mt-6 max-w-xl text-lg leading-relaxed text-text-muted">
                    One authenticated request returns up to 200 completed sales with price, date, condition, shipping
                    and seller. Fetched live from eBay at request time, never from a cache. Use it from your app, a
                    script, or an AI assistant.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <Link
                        to="/signin"
                        className="inline-flex items-center justify-center border border-primary bg-primary px-5 py-3 text-sm font-medium text-text-on-primary hover:border-primary-hover hover:bg-primary-hover"
                    >
                        Start free · {FREE_TIER.requests}
                    </Link>
                    <Link
                        to="/pricing"
                        className="inline-flex items-center justify-center border border-border-strong bg-surface px-5 py-3 text-sm font-medium text-text hover:border-secondary"
                    >
                        See pricing
                    </Link>
                    <Link to="/docs" className="text-sm text-text-muted underline-offset-4 hover:text-text hover:underline">
                        Read the docs
                    </Link>
                </div>
            </section>

            {/*
              Cost is answered here, above the fold, because that is what
              docs/feedbacks.md #2 asks of this page. It was a wrapping row of
              uppercase mono strings, which is legible but shapeless; as a ruled
              grid each fact gets a value the eye can land on and a label under
              it, and the six of them read as a spec sheet rather than a
              sentence that ran out of room. The tier decision still belongs to
              /pricing, linked above and from the header and footer.
            */}
            <section
                aria-label="At a glance"
                className="mt-14 grid grid-cols-2 border-t border-l border-border sm:grid-cols-3 lg:grid-cols-6"
            >
                {SPECS.map((spec) => (
                    <div key={spec.label} className="border-r border-b border-border px-4 py-5">
                        <p className="text-lg leading-none font-bold tracking-[-0.02em] text-text">{spec.value}</p>
                        <p className="eyebrow mt-2 text-text-muted">{spec.label}</p>
                    </div>
                ))}
            </section>

            <section className="mt-10">
                <SearchPlayground mode="demo" />
            </section>

            <section className="mt-24">
                <h2 className="text-section font-bold">What you get back</h2>
                {/*
                  This paragraph used to explain why `cardsOnPage` sits next to
                  `totalItems`. That distinction is real and it matters, but it
                  matters on /docs, to someone already integrating. Here it was
                  the first sentence a stranger read, and it answered a question
                  they had not thought to ask yet. The sample below shows the
                  fields; this line now says what they are for.
                */}
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-text-muted">
                    One request, one JSON response. Every sale comes back with what it sold for,
                    when it sold, its condition, the shipping cost and a link to the original
                    listing. Store it, chart it, or hand it to a model.
                </p>
                <div className="mt-6 grid gap-6 lg:grid-cols-2">
                    <CodeBlock label="Your request" code={curlSample} />
                    <CodeBlock label="What comes back" code={SAMPLE_SEARCH_JSON} />
                </div>
                <p className="mt-4 max-w-xl text-sm text-text-muted">
                    Full field reference on the{' '}
                    <Link
                        to="/docs/search"
                        className="text-text underline decoration-border-strong underline-offset-2 hover:decoration-text"
                    >
                        search endpoint docs
                    </Link>
                    .
                </p>
            </section>

            <section className="mt-24">
                {/*
                  "Frequently asked questions" rather than a phrasing of our
                  own. This heading is read by people scanning and by models
                  summarising the page, and both recognise the standard string.
                  Cleverness here costs comprehension and buys nothing.
                */}
                <h2 className="text-section font-bold">Frequently asked questions</h2>
                <dl className="mt-8 grid gap-x-12 gap-y-9 sm:grid-cols-2">
                    {FAQ.map((item, i) => (
                        <div key={item.question}>
                            <dt className="flex gap-3 font-semibold text-text">
                                <span className="eyebrow pt-1 text-text-muted tabular-nums">
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span>{item.question}</span>
                            </dt>
                            <dd className="mt-2 pl-8 text-sm leading-relaxed text-text-muted">{item.answer}</dd>
                        </div>
                    ))}
                </dl>
            </section>

            <section className="mt-24 border border-border bg-secondary px-6 py-10 text-bg sm:px-10 sm:py-14">
                <h2 className="max-w-[14ch] text-title text-balance">Get a key in about a minute.</h2>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-bg/70">
                    No card, no sales call, no eBay developer account. Enter an email, click the link, copy the key.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                        to="/signin"
                        className="inline-flex items-center justify-center border border-bg bg-bg px-5 py-3 text-sm font-medium text-text hover:bg-bg-sunk"
                    >
                        Get started free
                    </Link>
                    <Link
                        to="/docs/search"
                        className="inline-flex items-center justify-center border border-white/30 px-5 py-3 text-sm font-medium text-bg hover:border-bg"
                    >
                        Try the request shape
                    </Link>
                </div>
            </section>
        </div>
    );
}
